import { REQUEST_TIMEOUT, STAT_WEIGHT, STAT_TYPES } from "~/const";
import { fetchJSON, abortFetchSignal, getProfileErrors, getRequestLang } from "~/utils/utils";
import type { IGroup, IActivitCoefficient, IIngredientItem, IIngredientItemSend, IMealsListItem, IRecipesItem, IUserWeight, IStatItem, IGetMenuOutput, IUser, ISetUserData,
	IListStatType, ITagsItem, ITrainingPlan, IUnitsListItem, IRegister, IExcludedIngredient, IUserItem, IUsersFilters, IUsersSort, IUsersSummary, IUserDetail, IPermissionsItem, IFoodPlan, IListMenuOutput, IShoppingListItem,
	IRecipeRating, IRatedRecipe, IMailTemplateItem, IMailTemplate, TMailLang, IMenuDeletePreview, IUserMenuDays } from "./interfaces";
import type { IPaging } from "~/interfaces";
import { applyUserLang } from "~/stores/lang";

export function listStatsTypes(jwt: string) {
	return fetchJSON<Array<IListStatType>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listStatTypes`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
		}),
	}, []);
}

export function addStatsItem(jwt: string, user_id: number, type: number, value: number, date: string) {
	return fetchJSON<Array<IListStatType>>(`${window.pageSettings.SCRIPTS_PATH}index.php/addUserStat`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				type,
				value,
				date,
			},
		}),
	}, []);
}

export function listAllStats(jwt: string, user_id: number) {
	return fetchJSON<Array<IStatItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listUserStats`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
			},
		}),
	});
}

export function newestStat(jwt: string, user_id: number, stat_id: number) {
	return fetchJSON<null | IStatItem>(`${window.pageSettings.SCRIPTS_PATH}index.php/getUserLastStat`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				stat_id,
			},
		}),
	});
}

export async function userWeight(jwt: string, userId: number): Promise<IUserWeight> {
	const statsTypesData = await listStatsTypes(jwt);
	const weightType = statsTypesData.data.filter(item => item.type === STAT_WEIGHT)[0];

	if (!weightType) {
		return {
			weight: 0,
		};
	}

	const newestStatData = await newestStat(jwt, userId, weightType.stats_type_id);

	return {
		weight: newestStatData.data?.value ?? 0,
	};
}

// denni cil kalorii - stejna hodnota, ze ktere se generuje jidelnicek
export async function userGoalEnergy(jwt: string, userId: number): Promise<number> {
	const statsTypesData = await listStatsTypes(jwt);
	const energyType = statsTypesData.data.filter(item => item.type === STAT_TYPES.ENERGY)[0];

	if (!energyType) {
		return 0;
	}

	const newestStatData = await newestStat(jwt, userId, energyType.stats_type_id);

	return newestStatData.data?.value ?? 0;
}

async function completeUserData(jwt: string, jsd: IUser, weight?: number): Promise<IUser> {
	const output: IUser = {
		...jsd,
		jwt,
		birthdate: jsd?.birthdate || "",
		user_group: jsd?.user_group || 0,
		zipcode: jsd?.zipcode || "",
		errors: [],
	};

	let weightValue = weight;

	if (jwt && (!weightValue || weightValue <= 0)) {
		const weightData = await userWeight(jwt, output.id);

		weightValue = weightData.weight;
	}

	output.errors = getProfileErrors(output, weightValue);

	return output;
}

export function groupsList(jwt: string) {
	return fetchJSON<Array<IGroup>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listGroups`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
		}),
	}, []);
}

export function activityCoefficientList(jwt: string) {
	return fetchJSON<Array<IActivitCoefficient>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listActivityCoefficient`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
		}),
	}, []);
}


export function ingredientsList(jwt: string, filter?: string, paging?: IPaging) {
	return fetchJSON<Array<IIngredientItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listIngredients`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			filter,
			...paging || {},
		}),
	}, []);
}

export function addIngredient(jwt: string, newIngredient: Partial<IIngredientItemSend>) {
	delete newIngredient.id;
	delete newIngredient.energy;

	return fetchJSON<Array<IIngredientItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/addIngredient`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: newIngredient,
		}),
	});
}

export function removeIngredient(jwt: string, ingredientId: number) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/deleteIngredient`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				id: ingredientId,
			},
		}),
	});
}

export function addUserExcludedIngredient(jwt: string, user_id: number, ingredient_id: number) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/addUserExcludedIngredient`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				ingredient_id,
			},
		}),
	});
}

export function removeUserExcludedIngredient(jwt: string, user_id: number, ingredient_id: number) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/removeUserExcludedIngredient`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				ingredient_id,
			},
		}),
	});
}

export function addUserExcludedTag(jwt: string, user_id: number, tag_id: number) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/addUserExcludedTag`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				tag_id,
			},
		}),
	});
}

export function removeUserExcludedTag(jwt: string, user_id: number, tag_id: number) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/removeUserExcludedTag`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				tag_id,
			},
		}),
	});
}

export function mealsList(jwt: string) {
	return fetchJSON<Array<IMealsListItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listMeals`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
		}),
	}, []);
}

export function uploadPhoto(jwt: string, photoBase64: string, type: string) {
	return fetchJSON<string>(`${window.pageSettings.SCRIPTS_PATH}index.php/uploadImage`, {
		method: "POST",
		signal: null,
		body: JSON.stringify({
			jwt,
			image: {
				type,
				data: photoBase64,
			},
		}),
	});
}

export function recipesList(jwt: string, filter?: string, paging?: IPaging, noImage?: boolean) {
	return fetchJSON<Array<IRecipesItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listRecipes`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			filter,
			noImage,
			...paging || {},
		}),
	}, []);
}

export function recipeAdd(jwt: string, recipe: Partial<IRecipesItem>) {
	return fetchJSON<Array<IRecipesItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/addRecipe`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: recipe,
		}),
	});
}

export function recipeEdit(jwt: string, recipe: Partial<IRecipesItem>) {
	return fetchJSON<Array<IRecipesItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/editRecipe`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: recipe,
		}),
	});
}

export function recipeRemove(jwt: string, recipeId: number) {
	return fetchJSON<Array<IRecipesItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/deleteRecipe`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				id: recipeId,
			},
		}),
	});
}

export async function getShopingListPdf(jwt: string, user_id: number, fromRange: string, toRange: string, fileName: string): Promise<string> {
	try {
		const response = await fetch(`${window.pageSettings.SCRIPTS_PATH}getpdfShoppingList.php`, {
			method: "POST",
			headers: {
				"Accept": 'application/json',
				"X-Lang": getRequestLang(),
			},
			signal: abortFetchSignal(REQUEST_TIMEOUT),
			credentials: 'include',
			body: JSON.stringify({
				jwt,
				data: {
					user_id,
					from: fromRange,
					to: toRange,
				},
			}),
		});
		const blob = await response.blob();
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');

		link.href = url;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		link.remove();

		return "";
	} catch (exc) {
		return exc.message;
	}
}

export function userStat(jwt: string, user_id: number, stat_id: number) {
	return fetchJSON<Array<IStatItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/getUserStat`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				stat_id,
			},
		}),
	}, []);
}


export function tagsList(jwt: string, filter?: string, paging?: IPaging) {
	return fetchJSON<Array<ITagsItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listFTags`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			filter,
			...paging || {},
		}),
	}, []);
}

export function createTag(jwt: string, tag_name: string) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/addFTag`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				tag_name,
			},
		}),
	});
}

export function renameTag(jwt: string, tagId: number, tag_name: string) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/editFTag`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				id: tagId,
				tag_name,
			},
		}),
	});
}

export function removeTag(jwt: string, tagId: number) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/deleteFTag`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				id: tagId,
			},
		}),
	});
}

export function trainingPlan(jwt: string, userId: number) {
	return fetchJSON<ITrainingPlan>(`${window.pageSettings.SCRIPTS_PATH}index.php/getTrainingPlan`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id: userId,
			},
		}),
	});
}

export function editTrainingPlan(jwt: string, userId: number, data: ITrainingPlan) {
	// any - todo
	return fetchJSON<any>(`${window.pageSettings.SCRIPTS_PATH}index.php/editTrainingPlan`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id: userId,
				...data,
			},
		}),
	});
}

export function unitsList(jwt: string) {
	return fetchJSON<Array<IUnitsListItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listUnits`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
		}),
	}, []);
}

export async function login(email: string, password: string) {
	const jsonData = await fetchJSON<IUser>(`${window.pageSettings.SCRIPTS_PATH}index.php/login`, {
		method: "POST",
		body: JSON.stringify({
			email,
			password,
		}),
	});

	if (jsonData.noErrors) {
		jsonData.data = await completeUserData(jsonData.jwt, jsonData.data);
		applyUserLang(jsonData.data.lang);
	}


	return jsonData;
}

export async function auth(jwt: string) {
	const jsonData = await fetchJSON<IUser>(`${window.pageSettings.SCRIPTS_PATH}index.php/auth`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
		}),
	});

	if (!jsonData.logged) {
		jsonData.errors = ["Not logged!"];
		jsonData.noErrors = false;
	}

	if (jsonData.noErrors) {
		jsonData.data = await completeUserData(jsonData.jwt, jsonData.data);
		applyUserLang(jsonData.data.lang);
	}

	return jsonData;
}

export async function register(registerData: IRegister) {
	const jsonData = await fetchJSON<IUser>(`${window.pageSettings.SCRIPTS_PATH}index.php/register`, {
		method: "POST",
		body: JSON.stringify(registerData),
	});

	if (jsonData.noErrors) {
		jsonData.data = await completeUserData(jsonData.jwt, jsonData.data);
	}

	return jsonData;
}

export async function setUserData(jwt: string, userData: ISetUserData) {
	const jsonData = await fetchJSON<IUser>(`${window.pageSettings.SCRIPTS_PATH}index.php/setUserData`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: userData,
		}),
	});

	if (jsonData.noErrors) {
		jsonData.data = await completeUserData(jsonData.jwt, jsonData.data, userData.weight);
	}

	return jsonData;
}

// jazyk aplikace prihlaseneho uzivatele (uzivatel podle jwt)
export function setUserLang(jwt: string, lang: string) {
	return fetchJSON<null>(`${window.pageSettings.SCRIPTS_PATH}index.php/setUserLang`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				lang,
			},
		}),
	});
}

export function computeEnergy(jwt: string, user_id: number) {
	return fetchJSON<number>(`${window.pageSettings.SCRIPTS_PATH}index.php/computeEnergy`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
			},
		}),
	});
}

export function getMenu(jwt: string, user_id: number, date: string) {
	return fetchJSON<IGetMenuOutput>(`${window.pageSettings.SCRIPTS_PATH}index.php/getMenu`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				date,
			},
		}),
	});
}

export function listMenu(jwt: string, user_id: number) {
	return fetchJSON<Array<IListMenuOutput>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listMenu`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
			},
		}),
	}, []);
}


export function listUserExcludedIngredients(jwt: string, user_id: number) {
	return fetchJSON<Array<IExcludedIngredient>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listUserExcludedIngredients`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
			},
		}),
	}, []);
}

export function listUserExcludedTags(jwt: string, user_id: number) {
	return fetchJSON<Array<ITagsItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listUserExcludedTags`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
			},
		}),
	}, []);
}

// vymena jednoho jidla, vraci dny, ktere se zmenily
export function regenerateMeal(jwt: string, user_id: number, date: string, id_food_tag: number) {
	return fetchJSON<Array<string>>(`${window.pageSettings.SCRIPTS_PATH}index.php/regenerateMeal`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				date,
				id_food_tag,
			},
		}),
	});
}

export function generateMenu(jwt: string, user_id: number, fromDate: string, toDate?: string) {
	return fetchJSON<null>(`${window.pageSettings.SCRIPTS_PATH}index.php/generateMenu`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				...fromDate && toDate
					? {
						from: fromDate,
						to: toDate,
					}
					: {
						date: fromDate,
					},
			},
		}),
	});
}

export function getShoppingList(jwt: string, user_id: number, fromDate: string, toDate: string) {
	return fetchJSON<Array<IShoppingListItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/getShoppingList`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				from: fromDate,
				to: toDate,
			},
		}),
	});
}

export function getRecipeById(jwt: string, recipeId: number) {
	return fetchJSON<IRecipesItem>(`${window.pageSettings.SCRIPTS_PATH}index.php/getRecipe`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				id: recipeId,
			},
		}),
	});
}

export function usersTotalCount(jwt: string, filters: IUsersFilters) {
	return fetchJSON<{ total: number }>(`${window.pageSettings.SCRIPTS_PATH}index.php/countUsers`, {
		method: "POST",
		body: JSON.stringify({ jwt, filters }),
	}, { total: 0 });
}

export function usersList(jwt: string, filters: IUsersFilters, sort: IUsersSort, paging?: IPaging) {
	return fetchJSON<Array<IUserItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listUsers`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			filters,
			sort,
			...paging || {},
		}),
	}, []);
}

// vsichni uzivatele podle filtru bez strankovani (CSV export)
export function usersExport(jwt: string, filters: IUsersFilters, sort: IUsersSort) {
	return fetchJSON<Array<IUserItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/exportUsers`, {
		method: "POST",
		body: JSON.stringify({ jwt, filters, sort }),
	}, []);
}

export function usersSummary(jwt: string) {
	return fetchJSON<IUsersSummary>(`${window.pageSettings.SCRIPTS_PATH}index.php/getUsersSummary`, {
		method: "POST",
		body: JSON.stringify({ jwt }),
	});
}

export function userDetail(jwt: string, userId: number) {
	return fetchJSON<IUserDetail>(`${window.pageSettings.SCRIPTS_PATH}index.php/getUserDetail`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				id: userId,
			},
		}),
	});
}

// odeslani e-mailu muze trvat dele nez bezny pozadavek - server ma SMTP limit 20 s, prohlizec ceka 60 s
const MAIL_SEND_TIMEOUT = 60000;

// POST {jwt, data} (administrace e-mailu a jidelnicku, smazani uctu)
function adminRequest<T>(action: string, jwt: string, data?: object, defaultData?: T, timeoutMs = REQUEST_TIMEOUT) {
	return fetchJSON<T>(`${window.pageSettings.SCRIPTS_PATH}index.php/${action}`, {
		method: "POST",
		body: JSON.stringify({ jwt, data }),
		signal: abortFetchSignal(timeoutMs),
	}, defaultData);
}

export function mailTemplatesList(jwt: string) {
	return adminRequest<Array<IMailTemplateItem>>("listMailTemplates", jwt, undefined, []);
}

export function mailTemplateGet(jwt: string, id: number) {
	return adminRequest<IMailTemplate>("getMailTemplate", jwt, { id });
}

export function mailTemplateSave(jwt: string, template: IMailTemplate) {
	return adminRequest<{ id: number }>("saveMailTemplate", jwt, template);
}

export function mailTemplateRemove(jwt: string, id: number) {
	return adminRequest<Record<string, never>>("deleteMailTemplate", jwt, { id });
}

// jeden e-mail jednomu uzivateli v jeho jazyce (rozesilani vola postupne s pauzou)
export function sendTemplateMail(jwt: string, template_id: number, user_id: number) {
	return adminRequest<{ email: string }>("sendTemplateMail", jwt, { template_id, user_id }, undefined, MAIL_SEND_TIMEOUT);
}

export function sendTestMail(jwt: string, template_id: number, lang: TMailLang, email: string) {
	return adminRequest<Record<string, never>>("sendTestMail", jwt, { template_id, lang, email }, undefined, MAIL_SEND_TIMEOUT);
}

// administrace jidelnicku
export function menuDeletePreview(jwt: string, days: number) {
	return adminRequest<IMenuDeletePreview>("getMenuDeletePreview", jwt, { days });
}

export function deleteOldMenus(jwt: string, days: number) {
	return adminRequest<{ rows: number; before: string }>("deleteOldMenus", jwt, { days });
}

export function userMenuDays(jwt: string, user_id: number) {
	return adminRequest<IUserMenuDays>("listUserMenuDays", jwt, { user_id });
}

export function deleteUserMenuDay(jwt: string, user_id: number, date: string) {
	return adminRequest<{ rows: number }>("deleteUserMenuDay", jwt, { user_id, date });
}

export function getListPermissions(jwt: string) {
	return fetchJSON<Array<IPermissionsItem>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listPermissions`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
		}),
	}, []);
}

export function setPermissions(jwt: string, user_id: number, permissions: number) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/setPermissions`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
				permissions,
			},
		}),
	});
}

export function setVerification(jwt: string, user_id: number) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/setVerification`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
			},
		}),
	});
}

export function verifyEmail(hashId: string) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/verifyEmail`, {
		method: "POST",
		body: JSON.stringify({
			data: hashId,
		}),
	});
}

export function unsubscribe(hashId: string) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/unsubscribe`, {
		method: "POST",
		body: JSON.stringify({
			data: hashId,
		}),
	});
}

export function requestPasswordReset(email: string) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/requestPasswordReset`, {
		method: "POST",
		body: JSON.stringify({
			data: email,
		}),
	});
}

export function setNewPassword(token: string, password: string) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/setNewPassword`, {
		method: "POST",
		body: JSON.stringify({
			data: {
				token,
				password,
			},
		}),
	});
}

export function resendVerifyEmail(jwt: string, user_id: number) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/resendEmail`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
			},
		}),
	});
}

export function reVerifyEmail(email: string) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/reVerifyEmail`, {
		method: "POST",
		body: JSON.stringify({
			data: email,
		}),
	});
}

export function setUserPhoto(jwt: string, photoPath: string) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/setUserPhoto`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				photo: photoPath,
			},
		}),
	});
}

// uzivatel maze vlastni ucet, heslo pro potvrzeni
// predplatne bez reklam (administrace)
export function setAdFree(jwt: string, user_id: number, ad_free: boolean) {
	return adminRequest<{ ad_free: number; ad_free_since: string | null }>("setAdFree", jwt, { user_id, ad_free });
}

export function deleteMyAccount(jwt: string, password: string) {
	return adminRequest<Record<string, never>>("deleteMyAccount", jwt, { password });
}

export function deleteUser(jwt: string, userId: number) {
	return fetchJSON<Record<string, never>>(`${window.pageSettings.SCRIPTS_PATH}index.php/deleteUser`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				id: userId,
			},
		}),
	});
}

export function getFoodPlan(jwt: string, user_id: number) {
	return fetchJSON<IFoodPlan>(`${window.pageSettings.SCRIPTS_PATH}index.php/getFoodPlan`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				user_id,
			},
		}),
	});
}

export function getRecipeRating(jwt: string, recipe_id: number) {
	return fetchJSON<IRecipeRating>(`${window.pageSettings.SCRIPTS_PATH}index.php/getRecipeRating`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				recipe_id,
			},
		}),
	});
}

// rating 1 = like, -1 = dislike, 0 = zrusit hodnoceni
export function setRecipeRating(jwt: string, recipe_id: number, rating: number) {
	return fetchJSON<IRecipeRating>(`${window.pageSettings.SCRIPTS_PATH}index.php/setRecipeRating`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
			data: {
				recipe_id,
				rating,
			},
		}),
	});
}

export function listMyRatings(jwt: string) {
	return fetchJSON<Array<IRatedRecipe>>(`${window.pageSettings.SCRIPTS_PATH}index.php/listMyRatings`, {
		method: "POST",
		body: JSON.stringify({
			jwt,
		}),
	}, []);
}
